import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Ticket, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  Eye,
  UserCheck,
  TrendingUp,
  Filter
} from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { usePermissions } from '@hooks/usePermissions';
import { ADMIN_PERMISSIONS, TICKET_STATUS, TICKET_PRIORITY, ADMIN_ROUTES } from '@constants/admin.constants';
import supportAdminService from '@services/admin/support.admin.service';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import LoadingSpinner from '@components/common/LoadingSpinner';
import StatsCard from '@components/admin/StatsCard';
import FilterBar from '@components/admin/FilterBar';
import Pagination from '@components/admin/Pagination';
import PermissionGuard from '@components/admin/PermissionGuard';
import toast from 'react-hot-toast';

const SupportManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    priority: 'all',
    category: 'all',
    assignedTo: 'all',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState(null);

  const statusFilters = [
    { value: 'all', label: 'All Tickets' },
    { value: TICKET_STATUS.OPEN, label: 'Open' },
    { value: TICKET_STATUS.IN_PROGRESS, label: 'In Progress' },
    { value: TICKET_STATUS.WAITING_FOR_USER, label: 'Waiting' },
    { value: TICKET_STATUS.RESOLVED, label: 'Resolved' },
  ];

  useEffect(() => {
    if (!hasPermission(ADMIN_PERMISSIONS.VIEW_SUPPORT)) {
      toast.error('Access denied');
      navigate('/admin/dashboard');
      return;
    }
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        supportAdminService.getStats(),
        supportAdminService.getAllTickets(filters)
      ]);

      if (statsRes.success) {
        setStats(statsRes?.data?.data);
      }

      if (ticketsRes.success) {
        setTickets(Array.isArray(ticketsRes.data?.data?.tickets) ? ticketsRes.data.data.tickets : []);
        setPagination(ticketsRes.data?.data?.pagination || {});
      }
    } catch (error) {
      toast.error('Failed to load support data');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleViewTicket = (ticketId) => {
    navigate(`${ADMIN_ROUTES.SUPPORT}/${ticketId}`);
  };

  const getStatusBadge = (status) => {
    const badges = {
      [TICKET_STATUS.OPEN]: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Open' },
      [TICKET_STATUS.IN_PROGRESS]: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'In Progress' },
      [TICKET_STATUS.WAITING_FOR_USER]: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Waiting' },
      [TICKET_STATUS.RESOLVED]: { bg: 'bg-green-100', text: 'text-green-700', label: 'Resolved' },
      [TICKET_STATUS.CLOSED]: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Closed' },
      [TICKET_STATUS.REOPENED]: { bg: 'bg-red-100', text: 'text-red-700', label: 'Reopened' },
    };
    const badge = badges[status] || badges[TICKET_STATUS.OPEN];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      [TICKET_PRIORITY.LOW]: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Low' },
      [TICKET_PRIORITY.MEDIUM]: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Medium' },
      [TICKET_PRIORITY.HIGH]: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
      [TICKET_PRIORITY.URGENT]: { bg: 'bg-red-100', text: 'text-red-700', label: 'Urgent' },
    };
    const badge = badges[priority] || badges[TICKET_PRIORITY.MEDIUM];
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <PermissionGuard requiredPermission={ADMIN_PERMISSIONS.VIEW_SUPPORT}>
      <div className="min-h-full bg-gray-50 p-3">
          {/* <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary-900 mb-2">
              Support Ticket Management
            </h1>
            <p className="text-primary-600">
              Manage and respond to user support tickets
            </p>
          </div> */}

          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
              <StatsCard
                title="Total Tickets"
                value={stats.totalTickets || 0}
                icon={Ticket}
                color="primary"
              />
              <StatsCard
                title="Open Tickets"
                value={stats.openTickets || 0}
                icon={Clock}
                color="yellow"
              />
              <StatsCard
                title="Resolved Today"
                value={stats.resolvedToday || 0}
                icon={CheckCircle}
                color="green"
              />
              <StatsCard
                title="Urgent"
                value={stats.urgentTickets || 0}
                icon={AlertCircle}
                color="red"
              />
            </div>
          )}

          <Card className="mb-3 p-4">
            <FilterBar
              searchValue={filters.search}
              onSearchChange={(value) => handleFilterChange('search', value)}
              filters={statusFilters}
              activeFilter={filters.status}
              onFilterChange={(value) => handleFilterChange('status', value)}
              placeholder="Search by ticket number, subject, or user..."
            />
          </Card>

          <Card className="overflow-hidden">
            <div className="p-3 border-b border-gray-200">
              <h2 className="text-base font-semibold text-primary-900">
                Support Tickets
              </h2>
            </div>

            {loading ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="p-8 text-center">
                <Ticket className="w-16 h-16 text-primary-300 mx-auto mb-4" />
                <p className="text-primary-600">No tickets found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ticket
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Subject
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Priority
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigned To
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {tickets.map((ticket) => (
                        <tr key={ticket.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {ticket.ticketNumber}
                            </div>
                            <div className="text-xs text-gray-500">
                              {ticket.category}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {ticket.user?.email || 'Unknown'}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {ticket.subject}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {getPriorityBadge(ticket.priority)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            {getStatusBadge(ticket.status)}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {ticket.assignedAdmin?.email || (
                                <span className="text-gray-400">Unassigned</span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleViewTicket(ticket.id)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {pagination && (
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.totalPages}
                    totalItems={pagination.total}
                    itemsPerPage={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </>
            )}
          </Card>
      </div>
    </PermissionGuard>
  );
};

export default SupportManagement;
